import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Plus, ListChecks, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { DailyNonNegotiables } from "@/components/daily-nonnegotiables";
import { VisionCompass } from "@/components/vision-compass";
import { DailyDrivers } from "@/components/daily-drivers";
import { TutorialCarousel } from "@/components/tutorial-carousel";
import { WeeklyReportExport } from "@/components/weekly-report-export";
import { FollowUpNudges } from "@/components/followup-nudges";
import { CeoPacing } from "@/components/ceo-pacing";
import { CeoAdvisor } from "@/components/ceo-advisor";
import { useTrackView } from "@/hooks/use-track-view";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({
    meta: [
      { title: "CEO Dashboard — Boardroom" },
      {
        name: "description",
        content:
          "One screen for your creative business: are you on pace, what's the bottleneck, and the three moves that grow revenue today.",
      },
      { property: "og:title", content: "CEO Dashboard — Boardroom" },
      {
        property: "og:description",
        content: "Your pace, your bottleneck, and today's highest-value moves — in one calm view.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Dashboard,
});

function Dashboard() {
  const { user } = useAuth();
  useTrackView("dashboard");
  if (!user) return null;

  return (
    <div className="space-y-5 md:space-y-6">
      <TutorialCarousel userId={user.id} />

      <header className="pb-1">
        <BoardroomHeader userId={user.id} />
      </header>

      {/* Tier 1 — are we on pace? */}
      <CeoPacing userId={user.id} />

      {/* Tier 2 — what should I do about it? */}
      <CeoAdvisor />

      {/* Tier 3 — today's command center */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="flex flex-col gap-4 md:gap-6">
          <DailyNonNegotiables userId={user.id} />
          <DailyDrivers userId={user.id} />
        </div>
        <div className="flex flex-col gap-4 md:gap-6">
          <FollowUpNudges />
          <TodayRadar userId={user.id} />
        </div>
      </div>

      <div className="flex justify-end">
        <WeeklyReportExport userId={user.id} />
      </div>
    </div>
  );
}

/* ---------- Header ---------- */

function BoardroomHeader({ userId }: { userId: string }) {
  const { data: profile } = useQuery({
    queryKey: ["profile-vision-header", userId],
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("one_year_vision, ten_year_vision")
        .eq("id", userId)
        .maybeSingle();
      return data;
    },
  });

  return (
    <VisionCompass oneYear={profile?.one_year_vision} tenYear={profile?.ten_year_vision} userId={userId} />
  );
}

/* ---------- Today's radar: urgent work + logging a win ---------- */

function TodayRadar({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [winOpen, setWinOpen] = useState(false);
  const [winText, setWinText] = useState("");

  const { data: tasks = [] } = useQuery({
    queryKey: ["tasks-preview", userId],
    queryFn: async () => {
      const { data } = await supabase
        .from("tasks")
        .select("id, title, done, due_date, priority")
        .eq("user_id", userId)
        .eq("done", false)
        .order("due_date", { nullsFirst: false })
        .limit(5);
      return data ?? [];
    },
  });

  const { data: blockers = [] } = useQuery({
    queryKey: ["blockers-preview", userId],
    queryFn: async () => {
      const { data } = await supabase
        .from("blockers")
        .select("id, title")
        .eq("user_id", userId)
        .neq("status", "resolved")
        .order("created_at", { ascending: false })
        .limit(3);
      return data ?? [];
    },
  });

  const logWin = useMutation({
    mutationFn: async (text: string) => {
      const { error } = await supabase.from("wins").insert({ user_id: userId, text });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wins"] });
      setWinText("");
      setWinOpen(false);
      toast.success("Win logged");
    },
  });

  async function completeTask(id: string) {
    await supabase.from("tasks").update({ done: true, completed_at: new Date().toISOString() }).eq("id", id);
    qc.invalidateQueries({ queryKey: ["tasks-preview"] });
  }

  return (
    <div className="boardroom-card p-4 md:p-5 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
          <ListChecks className="h-3 w-3" /> On the docket
        </p>
        <Link to="/tasks" className="text-[10px] uppercase tracking-wider text-[color:var(--gold)]">
          All tasks →
        </Link>
      </div>

      {tasks.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nothing on the docket.</p>
      ) : (
        <div className="space-y-2">
          {tasks.map((t: any) => {
            const overdue = t.due_date && t.due_date < today;
            return (
              <div key={t.id} className="flex items-start gap-2 text-xs">
                <Checkbox onCheckedChange={() => completeTask(t.id)} className="mt-0.5" />
                <div className={`flex-1 min-w-0 truncate ${overdue ? "text-[color:var(--danger)]" : ""}`}>
                  {t.title}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {blockers.length > 0 && (
        <div className="pt-3 border-t border-[color:var(--hairline)]">
          <p className="text-[10px] font-semibold text-[color:var(--danger)] uppercase tracking-widest flex items-center gap-1.5 mb-2">
            <AlertTriangle className="h-3 w-3" /> Blockers ({blockers.length})
          </p>
          <div className="space-y-1">
            {blockers.map((b: any) => (
              <div key={b.id} className="text-xs text-foreground/80 truncate">
                • {b.title}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="pt-3 border-t border-[color:var(--hairline)]">
        <button
          onClick={() => setWinOpen(true)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[color:var(--gold)] hover:text-[color:var(--gold)]/80"
        >
          <Plus className="h-3.5 w-3.5" /> Log a win
        </button>
      </div>

      <Dialog open={winOpen} onOpenChange={setWinOpen}>
        <DialogContent className="bg-[color:var(--surface-1)] border-[color:var(--hairline)]">
          <DialogHeader>
            <DialogTitle>What moved the needle?</DialogTitle>
          </DialogHeader>
          <Textarea value={winText} onChange={(e) => setWinText(e.target.value)} rows={4} placeholder="The win…" autoFocus />
          <Button
            onClick={() => winText.trim() && logWin.mutate(winText.trim())}
            disabled={!winText.trim() || logWin.isPending}
            className="bg-[color:var(--gold)] text-[color:var(--primary-foreground)]"
          >
            Log it
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
